import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {OnboardingLayout} from './OnboardingLayout';
import {Button, CategoryPill} from '@/components';
import {useUserStore} from '@/store';
import {
  trackDepartmentSelected,
  trackDepartmentDeselected,
  trackDepartmentsContinue,
  trackCollectionSelected,
  trackCollectionDeselected,
  trackOtherDepartmentSubmitted,
} from '@/services/analytics';
import styles from './Onboarding.module.css';

const DEPARTMENTS = [
  {icon: '👗', label: 'Fashion'},
  {icon: '🧸', label: 'Toys'},
  {icon: '💄', label: 'Beauty'},
  {icon: '🏠', label: 'Home'},
  {icon: '🧹', label: 'Household Essentials'},
  {icon: '📱', label: 'Electronics'},
  {icon: '🛒', label: 'Grocery'},
  {icon: '🐾', label: 'Pets'},
];

const COLLECTION = [
  {label: 'Women'},
  {label: 'Men'},
  {label: 'Kids'},
  {label: 'Unisex'},
  {label: 'None'},
];

export function Departments() {
  const navigate = useNavigate();
  const {selectedCategories, toggleCategory, selectedCollections, toggleCollection} = useUserStore();
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherValue, setOtherValue] = useState('');

  function handleToggleDepartment(label: string) {
    const isSelected = selectedCategories.includes(label);
    toggleCategory(label);

    const updatedDepartments = isSelected
      ? selectedCategories.filter((c) => c !== label)
      : [...selectedCategories, label];

    if (isSelected) {
      trackDepartmentDeselected(label, updatedDepartments);
    } else {
      trackDepartmentSelected(label, updatedDepartments);
    }
  }

  function handleToggleCollection(label: string) {
    const isSelected = selectedCollections.includes(label);
    toggleCollection(label);

    const updatedCollections = isSelected
      ? selectedCollections.filter((c) => c !== label)
      : [...selectedCollections, label];

    if (isSelected) {
      trackCollectionDeselected(label, updatedCollections);
    } else {
      trackCollectionSelected(label, updatedCollections);
    }
  }

  function handleOtherToggle() {
    if (showOtherInput) {
      setShowOtherInput(false);
      setOtherValue('');
    } else {
      setShowOtherInput(true);
    }
  }

  const canContinue = selectedCategories.length > 0 && selectedCollections.length > 0;

  function handleContinue() {
    const otherDepartments = otherValue
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    let allDepartments = [...selectedCategories];

    for (const custom of otherDepartments) {
      if (!allDepartments.includes(custom)) {
        toggleCategory(custom);
        allDepartments = [...allDepartments, custom];
        trackOtherDepartmentSubmitted(custom, allDepartments);
      }
    }

    trackDepartmentsContinue(allDepartments, selectedCollections);
    navigate('/connect-email');
  }

  return (
    <OnboardingLayout currentStep={2} totalSteps={4}>
      <h2 className={styles.stepTitle}>What do you shop for?</h2>
      <p className={styles.stepDescription}>
        Select the departments you'd like to see in your closet.
      </p>
      <div className={styles.categoryGrid}>
        {DEPARTMENTS.map((dept) => (
          <CategoryPill
            key={dept.label}
            icon={dept.icon}
            label={dept.label}
            selected={selectedCategories.includes(dept.label)}
            onClick={() => handleToggleDepartment(dept.label)}
          />
        ))}
        <CategoryPill
          icon="✏️"
          label="Other"
          selected={showOtherInput}
          onClick={handleOtherToggle}
        />
      </div>
      {showOtherInput && (
        <div className={styles.otherInput}>
          <input
            type="text"
            placeholder="Enter other departments separated by commas"
            value={otherValue}
            onChange={(e) => setOtherValue(e.target.value)}
            autoFocus
          />
        </div>
      )}

      <h2 className={styles.stepTitleSecond}>Who do you shop for?</h2>
      <p className={styles.stepDescription}>
        Select the collections that best fit your lifestyle.
      </p>
      <div className={styles.categoryGrid}>
        {COLLECTION.map((col) => (
          <CategoryPill
            key={col.label}
            label={col.label}
            selected={selectedCollections.includes(col.label)}
            onClick={() => handleToggleCollection(col.label)}
          />
        ))}
      </div>

      <div className={styles.buttonRow}>
        <Button onClick={handleContinue} disabled={!canContinue}>Continue</Button>
      </div>
      <button className={styles.skipLink} onClick={() => navigate('/onboarding')}>
        ← Go back
      </button>
    </OnboardingLayout>
  );
}
